import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ShieldCheck, Lock, Clock, ArrowRight, CreditCard, Download } from "lucide-react";
import Logo from "../components/Logo";
import StripeCheckoutPanel from "../components/StripeCheckoutPanel";
import GeideaCheckoutPanel from "../components/GeideaCheckoutPanel";
import PaymentBadges from "../components/PaymentBadges";
import { Input } from "../components/ui/input";
import { getOrder } from "../lib/api";
import { priceDisplay, pinkPriceLabel } from "../lib/format";
import { useGeo } from "../lib/useGeo";
import { startSessionTracker } from "../lib/sessionTracker";

const PINK_LOGO = "/pink/logo.png";

export default function Checkout() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const country = useGeo();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getOrder(orderId)
      .then((o) => setOrder(o))
      .catch(() => setError("الطلب غير موجود"));
    const stop = startSessionTracker("checkout");
    return stop;
  }, [orderId]);

  const goToSuccess = () => navigate(`/success/${orderId}`);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div className="bg-[#0B1D3A] border border-[#C5A059]/30 p-8 rounded-sm">
          <h1 className="font-display text-2xl text-white font-bold mb-2">عذراً</h1>
          <p className="text-[#A1B0C6]">{error}</p>
          <button onClick={() => navigate("/")} className="mt-5 text-[#C5A059] underline">العودة للصفحة الرئيسية</button>
        </div>
      </div>
    );
  }

  if (!order) {
    return <div className="min-h-screen flex items-center justify-center text-[#A1B0C6]">جاري التحميل...</div>;
  }

  const isGeidea = order.provider === "geidea";
  const isEgStripe = !isGeidea && typeof order.package === "string" && order.package.startsWith("pdf_audio_eg");
  const isPink = order.package === "pink_audio" || order.is_pink === true;
  const isAI = order.package === "ai_work_upgrade" || order.is_ai === true;

  // ───────────────────────── AI Work Upgrade — Executive Light Theme (English / USA) ─────────────────────────
  if (isAI) {
    return (
      <div
        className="min-h-screen bg-white text-[#111827] py-8 sm:py-10 px-5 sm:px-8"
        dir="ltr"
        lang="en"
        data-testid="ai-checkout-root"
        style={{ fontFamily: "'Inter','SF Pro Display',system-ui,-apple-system,sans-serif" }}
      >
        <div className="max-w-5xl mx-auto">
          <header className="flex items-center justify-between mb-7 sm:mb-10">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-md bg-[#0F172A] flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-[#C9A227]" />
              </div>
              <div className="leading-tight">
                <div className="font-bold text-[14px] text-[#0F172A]">AI Work Upgrade</div>
                <div className="text-[10px] text-[#6B7280] tracking-wide flex items-center gap-1">
                  <Lock className="w-2.5 h-2.5" /> Secure checkout
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate("/")}
              className="text-[11px] text-[#9CA3AF] hover:text-[#0F172A] flex items-center gap-1 transition-colors"
              data-testid="back-to-home"
            >
              <ArrowRight className="w-3 h-3 rotate-180" /> Back
            </button>
          </header>

          {/* Mobile-first ordering: payment column appears FIRST on mobile so
              the Apple Pay button is visible without scrolling. Desktop uses
              the natural 2/3 split with summary on the left. */}
          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">

            {/* Payment — order-1 (mobile first), order-2 on desktop */}
            <div className="lg:col-span-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-5 sm:p-7 order-1 lg:order-2" data-testid="payment-panel">
              {/* Instructor + price reinforcement strip (above the payment form) */}
              <div className="flex items-center justify-between flex-wrap gap-3 pb-4 mb-5 border-b border-[#E5E7EB]" data-testid="ai-checkout-trust-strip">
                <div className="flex items-center gap-2.5 text-[12px] text-[#374151]">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0F172A] to-[#1E293B] flex items-center justify-center text-[10px] font-black text-[#C9A227] shrink-0">JE</div>
                  <div className="leading-tight">
                    <div className="font-black text-[#0F172A] text-[13px] sm:text-[14px]">AI Work Upgrade Full System</div>
                    <div className="text-[11px] text-[#6B7280] mt-0.5">
                      by <span className="font-bold text-[#0F172A]">Jackie Escalera</span> · Data &amp; AI Consultant
                    </div>
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[11px] text-[#6B7280] line-through">$129</span>
                  <span className="text-[#0F172A] font-black text-lg">$49</span>
                  <span className="text-[10px] text-[#6B7280]">· approx. AED 180</span>
                  <span className="bg-[#C9A227] text-[#0F172A] text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-wider" data-testid="ai-checkout-save-badge">Save $80</span>
                </div>
              </div>

              {/* Delivery promise — highlighted above the Google Pay button.
                  UAE buyers explicitly need to see this to overcome "is this another
                  subscription trap?" hesitation right before tapping the wallet. */}
              <div
                className="mb-5 bg-[#FFF9E6] border border-[#C9A227]/40 rounded-lg px-4 py-3.5"
                data-testid="ai-delivery-highlight"
              >
                <div className="flex items-start gap-2.5">
                  <Download className="w-4 h-4 text-[#0F766E] mt-0.5 shrink-0" />
                  <div className="leading-snug">
                    <div className="text-[14px] sm:text-[15px] font-black text-[#0F172A]">
                      Instant direct download link right after payment — <span className="text-[#0F766E]">no subscription required</span>
                    </div>
                    <div className="text-[12px] sm:text-[13px] text-[#374151] mt-1">
                      Full course + 50-prompt library + work templates + before/after examples
                    </div>
                  </div>
                </div>
              </div>

              <StripeCheckoutPanel
                orderId={orderId}
                amount={order.amount}
                pkg={order.package}
                onSuccess={goToSuccess}
                displayPriceOverride={{ primary: "$49", secondary: null, currencyShort: "USD", number: 49 }}
                themeAI
              />

              {/* 14-day money-back guarantee — placed directly under the Pay button
                  for maximum reassurance at the decision moment. */}
              <div
                className="mt-5 flex items-center gap-2.5 bg-[#0F766E]/5 border border-[#0F766E]/30 rounded-md px-3 py-2.5"
                data-testid="ai-guarantee"
              >
                <ShieldCheck className="w-4 h-4 text-[#0F766E] shrink-0" />
                <div className="text-[12px] leading-snug">
                  <span className="font-bold text-[#0F766E]">14-day money-back guarantee.</span>
                  <span className="text-[#374151]"> If it&apos;s not useful, request a refund.</span>
                </div>
              </div>

              <p className="mt-4 text-[11px] text-[#9CA3AF] leading-relaxed text-center">
                Statement shows <span className="text-[#374151] font-semibold">AI WORK UPGRADE</span>. You receive access immediately after payment.
              </p>
            </div>

            {/* Order summary — order-2 (mobile second), order-1 on desktop */}
            <div className="lg:col-span-2 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-5 sm:p-7 h-fit order-2 lg:order-1" data-testid="order-summary">
              <h2 className="font-black text-[17px] sm:text-xl mb-4 text-[#0F172A]">Order summary</h2>
              <div className="flex items-start justify-between border-b border-[#E5E7EB] pb-4 mb-4">
                <div>
                  <div className="font-bold text-[#0F172A] leading-snug">{order.package_name}</div>
                  <div className="text-sm text-[#6B7280] mt-1">Instant access to course materials &amp; templates</div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="text-xs text-[#6B7280] line-through">$129</div>
                  <div className="text-lg font-black text-[#C9A227]">$49</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-[#0F172A] font-bold">
                <span>Total</span>
                <div className="text-right">
                  <span className="text-2xl">$49 USD</span>
                  <div className="text-[11px] text-[#6B7280] font-normal mt-0.5">approx. AED 180</div>
                </div>
              </div>

              <div className="mt-5 bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid="delivery-promise">
                <div className="flex items-start gap-2.5">
                  <Download className="w-5 h-5 text-[#0F766E] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[#0F172A] font-bold text-[15px] leading-snug">
                      Instant direct download link right after payment
                    </div>
                    <div className="text-[#6B7280] text-[13px] mt-1.5 leading-snug">
                      Full course + 50-prompt library + work templates + before/after examples
                    </div>
                  </div>
                </div>
              </div>

              <ul className="mt-5 space-y-2 text-[13px] text-[#6B7280]">
                <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0F766E] shrink-0" /> Secure checkout. Card, Apple Pay, Google Pay.</li>
                <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#0F766E] shrink-0" /> Instant access after payment</li>
                <li className="flex items-center gap-2"><Lock className="w-4 h-4 text-[#0F766E] shrink-0" /> One-time payment — no subscription</li>
              </ul>

              <div className="mt-5 pt-5 border-t border-[#E5E7EB]">
                <div className="text-[10px] text-[#6B7280] tracking-[0.2em] text-center mb-2">ACCEPTED PAYMENT METHODS</div>
                <PaymentBadges emphasizeWallets country={country} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────── Pink-themed Checkout (full pink palette) ─────────────────────────
  if (isPink) {
    const pink = pinkPriceLabel(country);
    return (
      <div
        className="min-h-screen bg-[#FBF8F6] text-[#3E2F32] py-10 px-5 sm:px-8"
        dir="rtl"
        lang="ar"
        data-testid="pink-checkout-root"
        style={{ fontFamily: "'IBM Plex Sans Arabic','Tajawal',system-ui,sans-serif" }}
      >
        <div className="max-w-5xl mx-auto">
          <header className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-3">
              <img src={PINK_LOGO} alt="الكتاب الوردي" className="h-12 w-12 sm:h-14 sm:w-14 object-contain" />
              <span className="font-display text-lg sm:text-xl font-bold text-[#3E2F32]">الكتاب الوردي</span>
            </div>
            <button
              onClick={() => navigate("/pink")}
              className="text-sm text-[#5A4A4E] hover:text-[#8D6E79] flex items-center gap-2"
              data-testid="back-to-home"
            >
              <ArrowRight className="w-4 h-4 rotate-180" /> العودة
            </button>
          </header>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Order summary — pink */}
            <div
              className="lg:col-span-2 bg-white border border-[#E8DDD8] rounded-3xl p-7 h-fit shadow-[0_20px_50px_rgba(141,110,121,0.10)]"
              data-testid="order-summary"
            >
              <h2 className="font-display text-xl font-bold mb-5 text-[#3E2F32]">ملخص الطلب</h2>
              <div className="flex items-start justify-between border-b border-[#E8DDD8] pb-4 mb-4">
                <div>
                  <div className="font-bold text-[#3E2F32] leading-snug">{order.package_name}</div>
                  <div className="text-sm text-[#5A4A4E] mt-1">رابط تحميل مباشر على جوالك</div>
                </div>
                <div className="text-left">
                  <div className="text-lg font-black text-[#8D6E79]">{pink.primary}</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-[#3E2F32] font-bold">
                <span>الإجمالي</span>
                <span className="text-2xl font-display">{pink.primary}</span>
              </div>

              {/* Delivery promise — pink palette */}
              <div
                className="mt-6 bg-[#F3E6E8]/60 border border-[#D8B7BE] rounded-2xl p-4 text-right"
                data-testid="delivery-promise"
              >
                <div className="flex items-start gap-2.5">
                  <Download className="w-5 h-5 text-[#8D6E79] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[#3E2F32] font-bold text-base leading-snug">
                      تحميل فوري للبرنامج الصوتي بعد إتمام الدفع
                      {" "}
                      <span className="bg-[#C8A977]/30 text-[#8D6E79] px-1.5 py-0.5 rounded-md font-black">بخصوصية تامة</span>
                    </div>
                    <div className="text-[#8D6E79] text-sm mt-1.5 font-semibold leading-snug">
                      جميع الجلسات الصوتية تصلك على نفس الصفحة
                    </div>
                  </div>
                </div>
              </div>

              <ul className="mt-6 space-y-2.5 text-sm text-[#5A4A4E]">
                <li className="flex items-center gap-2 justify-end"><ShieldCheck className="w-4 h-4 text-[#C8A977]" /> ضمان الخصوصية</li>
                <li className="flex items-center gap-2 justify-end"><Clock className="w-4 h-4 text-[#C8A977]" /> تسليم فوري</li>
                <li className="flex items-center gap-2 justify-end"><Lock className="w-4 h-4 text-[#C8A977]" /> دفع آمن ومشفّر</li>
                <li className="flex items-center gap-2 justify-end"><ShieldCheck className="w-4 h-4 text-[#C8A977]" /> ضمان استرداد خلال 7 أيام</li>
              </ul>

              <div className="mt-5 pt-5 border-t border-[#E8DDD8]">
                <div className="text-[10px] text-[#8D6E79] tracking-[0.2em] text-center mb-2">طرق الدفع المقبولة</div>
                <PaymentBadges emphasizeWallets country={country} />
              </div>
            </div>

            {/* Payment — pink */}
            <div
              className="lg:col-span-3 bg-white border border-[#E8DDD8] rounded-3xl p-7 shadow-[0_20px_50px_rgba(141,110,121,0.10)]"
              data-testid="payment-panel"
            >
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="w-5 h-5 text-[#8D6E79]" />
                <h2 className="font-display text-xl font-bold text-[#3E2F32]">الدفع</h2>
              </div>
              <p className="text-xs text-[#5A4A4E] mb-5">
                بطاقة ائتمان · Apple Pay · Google Pay — سريع وآمن، لا حاجة لإنشاء حساب
              </p>

              <StripeCheckoutPanel
                orderId={orderId}
                amount={order.amount}
                pkg={order.package}
                onSuccess={goToSuccess}
                displayPriceOverride={pink}
                themePink
              />

              <p className="mt-6 text-xs text-[#5A4A4E] leading-relaxed text-center">
                ستظهر العملية في كشف حسابك باسم <span className="text-[#8D6E79] font-bold">BLUEBOOK</span>.
                ستحصلين على رابط التحميل مباشرة بعد إتمام الدفع
                {" "}
                <span className="bg-[#C8A977]/30 text-[#8D6E79] px-1.5 py-0.5 rounded-md font-bold">بخصوصية تامة</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────── Default (Gulf / EG) Checkout — unchanged dark theme ─────────────────────────
  let priceInfo;
  if (isGeidea) {
    priceInfo = { primary: `${Math.round(order.amount)} جنيه`, secondary: null, currencyShort: "جنيه" };
  } else if (isEgStripe) {
    const egp = order.display_egp || 499;
    priceInfo = { primary: `${Math.round(egp)} جنيه`, secondary: null, currencyShort: "جنيه" };
  } else {
    priceInfo = priceDisplay(order.amount, country);
  }

  return (
    <div className="min-h-screen bg-[#040914] text-white py-10 px-5 sm:px-8" dir="rtl">
      <div className="max-w-5xl mx-auto">
        <header className="flex items-center justify-between mb-10">
          <Logo className="h-12 w-12" />
          <button
            onClick={() => navigate(isGeidea || isEgStripe ? "/eg" : "/")}
            className="text-sm text-[#A1B0C6] hover:text-[#C5A059] flex items-center gap-2"
            data-testid="back-to-home"
          >
            <ArrowRight className="w-4 h-4 rotate-180" /> العودة
          </button>
        </header>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Order summary */}
          <div className="lg:col-span-2 bg-[#0B1D3A]/70 border border-[#C5A059]/25 rounded-sm p-7 h-fit" data-testid="order-summary">
            <h2 className="font-display text-xl font-bold mb-5">ملخص الطلب</h2>
            <div className="flex items-start justify-between border-b border-[#C5A059]/15 pb-4 mb-4">
              <div>
                <div className="font-bold text-white">{order.package_name}</div>
                <div className="text-sm text-[#A1B0C6] mt-1">تسليم فوري بعد إتمام الدفع</div>
              </div>
              <div className="text-left">
                <div className="text-lg font-black text-[#C5A059]">{priceInfo.primary}</div>
                {priceInfo.secondary && (
                  <div className="text-[11px] text-[#A1B0C6] mt-0.5">{priceInfo.secondary}</div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between text-white font-bold">
              <span>الإجمالي</span>
              <span className="text-left">
                <span className="text-2xl">{priceInfo.primary}</span>
                {priceInfo.secondary && (
                  <span className="block text-xs text-[#C5A059]/85 font-semibold mt-0.5">{priceInfo.secondary} USD</span>
                )}
              </span>
            </div>

            <div
              className="mt-6 bg-[#C5A059]/10 border border-[#C5A059]/40 rounded-sm p-4 text-right"
              data-testid="delivery-promise"
            >
              <div className="flex items-start gap-2.5">
                <Download className="w-5 h-5 text-[#C5A059] mt-0.5 shrink-0" />
                <div>
                  <div className="text-white font-bold text-base leading-snug">
                    تحميل فوري للكتاب مباشرة بعد إتمام الدفع
                    {" "}
                    <span className="bg-[#C5A059]/25 text-[#C5A059] px-1.5 py-0.5 rounded-sm font-black">بخصوصية تامة</span>
                  </div>
                  <div className="text-[#C5A059] text-sm mt-1.5 font-semibold leading-snug">
                    PDF + الملحق الصوتي يصلانك على نفس الصفحة
                  </div>
                </div>
              </div>
            </div>

            <ul className="mt-6 space-y-2 text-sm text-[#A1B0C6]">
              <li className="flex items-center gap-2 justify-end"><ShieldCheck className="w-4 h-4 text-[#C5A059]" /> ضمان الخصوصية</li>
              <li className="flex items-center gap-2 justify-end"><Clock className="w-4 h-4 text-[#C5A059]" /> تسليم فوري</li>
              <li className="flex items-center gap-2 justify-end"><Lock className="w-4 h-4 text-[#C5A059]" /> دفع آمن ومشفّر</li>
              <li className="flex items-center gap-2 justify-end"><ShieldCheck className="w-4 h-4 text-[#C5A059]" /> ضمان استرداد خلال 7 أيام</li>
            </ul>

            {!isGeidea && !isEgStripe && (
              <div className="mt-5 pt-5 border-t border-[#C5A059]/15">
                <div className="text-[10px] text-[#A1B0C6] tracking-[0.2em] text-center mb-2">طرق الدفع المقبولة</div>
                <PaymentBadges emphasizeWallets />
              </div>
            )}
          </div>

          {/* Payment */}
          <div className="lg:col-span-3 bg-[#0B1D3A]/70 border border-[#C5A059]/25 rounded-sm p-7" data-testid="payment-panel">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="w-5 h-5 text-[#C5A059]" />
              <h2 className="font-display text-xl font-bold">الدفع</h2>
            </div>
            <p className="text-xs text-[#A1B0C6] mb-5">
              {isGeidea
                ? "بطاقة · Apple Pay · المحافظ الإلكترونية — سريع وآمن، لا حاجة لإنشاء حساب"
                : isEgStripe
                ? "بطاقة · Apple Pay — سريع وآمن، لا حاجة لإنشاء حساب"
                : "بطاقة ائتمان · Apple Pay · Google Pay — سريع وآمن، لا حاجة لإنشاء حساب"}
            </p>

            {isGeidea ? (
              <GeideaCheckoutPanel
                orderId={orderId}
                onSuccess={goToSuccess}
              />
            ) : (
              <StripeCheckoutPanel
                orderId={orderId}
                amount={order.amount}
                pkg={order.package}
                onSuccess={goToSuccess}
                displayPriceOverride={isEgStripe ? priceInfo : undefined}
              />
            )}

            <p className="mt-6 text-xs text-[#A1B0C6] leading-relaxed text-center">
              ستظهر العملية في كشف حسابك باسم <span className="text-[#C5A059] font-bold">BLUEBOOK</span>.
              ستحصل على رابط التحميل مباشرة بعد إتمام الدفع
              {" "}
              <span className="bg-[#C5A059]/20 text-[#C5A059] px-1.5 py-0.5 rounded-sm font-bold">بخصوصية تامة</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
